import React, { useEffect, useState } from "react";
import { FaCheckCircle, FaChevronRight, FaShieldAlt } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "../../../api/axios";
import { BenefitBadge } from "../../../components/card/BenefitBadgeCard";
import HeaderWithBack from "../../../components/HeaderWithBack";
import { useCheckout } from "../../../hooks/useCheckout";
import { usePayment } from "../../../hooks/usePayment";
import { useAuthStore } from "../../../stores/authStores";
import { useCheckoutStore } from "../../../stores/checkoutStore";
import { formatRupiah } from "../../../utils/formatPrice";
import FullscreenLoading from "../../Prescription/Components/FullScreenLoader";
import { assetUrl, imageUrl } from "../../../utils/assets";
import PendingPaymentModal from "../../../components/modal/PendingPaymentModal";
import PendingPaymentCard from "../../../components/card/PendingPaymentCard";
import DoctorUnavailableModal from "../../../components/modal/DoctorUnavailableModal";
import Confirm from "../../../components/modal/Confirm";
import { usePendingPayment } from "../../../hooks/usePendingPayment";
import { cancelPendingPayment, PendingPayment } from "../../../services/pendingPaymentService";

const DOCTOR_UNAVAILABLE_CODES = ["DOCTOR_BUSY", "DOCTOR_OFFLINE"];

const CheckoutTeleconsultation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { practitionerRoleId } = useParams<{ practitionerRoleId: string }>();
  const userData = useAuthStore((state) => state.user);
  const authToken = useAuthStore((state) => state.token);

  const selectedPayment = useCheckoutStore((s) => s.selectedPayment);
  const selectedVoucher = useCheckoutStore((s) => s.selectedVoucher);
  const setPayment = useCheckoutStore((s) => s.setPayment);
  const setVoucher = useCheckoutStore((s) => s.setVoucher);
  const [toast, setToast] = useState<{ message: string; type: "error" | "success" } | null>(null);
  const [voucherCode, setVoucherCode] = useState(selectedVoucher?.code || "");
  const [applyingVoucher, setApplyingVoucher] = useState(false);
  const [showRemoveVoucherConfirm, setShowRemoveVoucherConfirm] = useState(false);
  const [pendingPayment, setPendingPayment] = useState<PendingPayment | null>(null);
  const [cancellingPending, setCancellingPending] = useState(false);
  const [doctorUnavailable, setDoctorUnavailable] = useState<{ title: string; message: string } | null>(null);
  const [showCancelActiveConfirm, setShowCancelActiveConfirm] = useState(false);

  // Pesanan yang sudah masuk Duitku tapi belum dibayar (pasien keluar dari halaman Duitku).
  const { data: activePending, refetch: refetchPending } = usePendingPayment(authToken);

  const isAuto = practitionerRoleId === 'auto';
  const { data: checkoutInfo, isLoading, error: checkoutError } = useCheckout({
    authToken,
    practitionerRoleId,
    isAuto,
    selectedVoucher,
    selectedPayment,
  });
  const { paying, createPayment } = usePayment({
    authToken,
    navigate,
  });

  const showToast = (message: string, type: "error" | "success" = "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const getVoucherUnavailableMessage = (errorData?: any) => {
    if (!errorData) return "Voucher sudah tidak tersedia.";

    const code = errorData.code;
    const message = errorData.message;

    switch (code) {
      case "VOUCHER_EXPIRED":
        return "Voucher telah kadaluarsa. Silakan hubungi admin untuk mendapatkan kode baru.";
      case "VOUCHER_USED_UP":
        return "Kuota voucher ini telah habis digunakan.";
      case "VOUCHER_UNAVAILABLE":
        return message || "Voucher tidak ditemukan atau tidak berlaku untuk pasien ini.";
      default:
        return message || "Voucher sudah tidak tersedia.";
    }
  };

  const serviceType = location.state?.serviceType || "chat";
  const stateVoucher = location.state?.selectedVoucher;
  const statePayment = location.state?.selectedPayment;
  const getPertanyaan = (): any => {
    if (location.state?.pertanyaan) return location.state.pertanyaan;
    const saved = localStorage.getItem("teleconsult_disclaimer");
    if (saved) { try { return JSON.parse(saved); } catch {} }
    return { berat: "", tinggi: "", lamaSakit: "", minumObat: "", riwayatCovid: "" };
  };
  const pertanyaan = getPertanyaan();

  useEffect(() => {
    if (stateVoucher) {
      setVoucher(stateVoucher);
    }
    if (statePayment) {
      setPayment(statePayment);
    }
  }, [setPayment, setVoucher, statePayment, stateVoucher]);

  useEffect(() => {
    if (checkoutInfo?.voucher && checkoutInfo.voucher.id !== selectedVoucher?.id) {
      setVoucher(checkoutInfo.voucher);
      setVoucherCode(checkoutInfo.voucher.code);
    }
  }, [checkoutInfo?.voucher, selectedVoucher?.id, setVoucher]);

  useEffect(() => {
    const error: any = checkoutError;
    if (error?.response?.data) {
      const errorData = error.response.data;
      if (errorData.code === "VOUCHER_UNAVAILABLE" || errorData.code === "VOUCHER_EXPIRED" || errorData.code === "VOUCHER_USED_UP") {
        setVoucher(null);
        setVoucherCode("");

        // Hanya tampilkan toast jika user memang memiliki voucher yang dipilih,
        // untuk menghindari pesan error muncul otomatis saat inisialisasi halaman.
        if (selectedVoucher) {
          showToast(`${getVoucherUnavailableMessage(errorData)} Total pembayaran diperbarui.`);
        }
      }
    }
  }, [checkoutError, setVoucher, selectedVoucher]);

  if (isLoading || !checkoutInfo) {
    return <FullscreenLoading />;
  }

  const consultationPrice =
    serviceType === "video"
      ? checkoutInfo.doctor.price_video
      : checkoutInfo.doctor.price_chat;

  const transactionFee  = checkoutInfo.transaction_fee;
  const subtotal = consultationPrice + transactionFee;
  const voucherDiscount = checkoutInfo?.discount ?? 0;
  const benefitInfo     = checkoutInfo.benefit;
  const totalAfterDiscount = checkoutInfo.total;
  const isFree = totalAfterDiscount === 0;
  // Backend adalah sumber kebenaran: benefit hanya tampil jika voucher
  // berhasil divalidasi dan response checkout mengembalikan voucher aktif.
  const isInsuranceCovered = Boolean(checkoutInfo.voucher?.id && benefitInfo?.eligible);
  const totalSavings = Math.max(subtotal - totalAfterDiscount, 0);
  const paymentWindowMinutes = checkoutInfo.payment_window_minutes;

  const chooseOtherDoctor = () => {
    setDoctorUnavailable(null);
    navigate("/list-doctor");
  };

  const cancelActivePending = async () => {
    if (!activePending || !authToken) return;
    setCancellingPending(true);
    try {
      await cancelPendingPayment(activePending.teleconsult_id, authToken);
      showToast("Pesanan sebelumnya dibatalkan.", "success");
    } catch (err: any) {
      showToast(err.response?.data?.message || "Gagal membatalkan pesanan");
    } finally {
      setCancellingPending(false);
      setShowCancelActiveConfirm(false);
      refetchPending();
    }
  };

  const handleApplyVoucher = async () => {
    const code = voucherCode.trim();
    if (!code) {
      showToast("Masukkan kode voucher");
      return;
    }

    setApplyingVoucher(true);
    try {
      const response = await axios.get("/voucher", {
        params: { voucher_code: code },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const voucher = response.data?.data?.[0];
      if (!voucher) {
        setVoucher(null);
        showToast("Voucher tidak ditemukan atau tidak berlaku untuk pasien ini.");
        return;
      }

      setVoucher(voucher);
      setVoucherCode(voucher.code);
      showToast("Voucher dipilih. Voucher akan diverifikasi kembali saat pembayaran.", "success");
    } catch (err: any) {
      setVoucher(null);
      const errorData = err.response?.data;
      showToast(getVoucherUnavailableMessage(errorData));
    } finally {
      setApplyingVoucher(false);
    }
  };

  const handleRemoveVoucher = () => {
    setVoucher(null);
    setVoucherCode("");
    setShowRemoveVoucherConfirm(false);
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: { ...(location.state || {}), selectedVoucher: null },
    });
    showToast("Voucher dilepas. Anda bisa memasukkan kode voucher lagi sebelum membayar.", "success");
  };

  const handleSubmitPayment = async () => {
    if (!isFree && !selectedPayment) {
      showToast("Pilih metode pembayaran dulu");
      return;
    }
    if (!pertanyaan.berat || !pertanyaan.tinggi) {
      showToast("Data berat dan tinggi badan belum lengkap.");
      return;
    }

    try {
      const payload: any = {
        user_id: userData.id,
        practitioner_role_id: isAuto ? null : practitionerRoleId,
        doctor_assign_status: isAuto ? "unassigned" : "assigned",
        pertanyaan,
        voucher_id: selectedVoucher?.id || null,
        total_amount: totalAfterDiscount,
      };

      if (!isFree) {
        payload.payment_method_id = selectedPayment?.id;
      }
      await createPayment({
        payload,
        checkoutInfo,
        onVoucherChanged: () => {
          setVoucher(null);
          setVoucherCode("");
          showToast("Voucher sudah tidak tersedia. Total pembayaran diperbarui.");
        },
      });
    } catch (err: any) {
      if (err.response?.status === 409 && err.response?.data?.code === "PENDING_PAYMENT_EXISTS") {
        setPendingPayment(err.response.data.pending_payment);
        return;
      }

      if (DOCTOR_UNAVAILABLE_CODES.includes(err.response?.data?.code)) {
        const isOffline = err.response.data.code === "DOCTOR_OFFLINE";
        setDoctorUnavailable({
          title: isOffline ? "Dokter Sedang Offline" : "Slot Dokter Penuh",
          message: err.response.data.message
            || "Slot konsultasi dokter ini sedang penuh. Silakan pilih dokter lain atau coba beberapa saat lagi.",
        });
        return;
      }

      if (err.response?.data?.code === "VOUCHER_UNAVAILABLE" || err.response?.data?.code === "VOUCHER_EXPIRED" || err.response?.data?.code === "VOUCHER_USED_UP") {
        setVoucher(null);
        setVoucherCode("");
        showToast(`${getVoucherUnavailableMessage(err.response.data)} Silakan lanjutkan dengan pembayaran reguler.`);
        return;
      }

      showToast(err.response?.data?.message || "Payment gagal");
    }
  };

  const handleCancelPendingAndPay = async () => {
    if (!pendingPayment || !authToken) return;
    setCancellingPending(true);
    try {
      await cancelPendingPayment(pendingPayment.teleconsult_id, authToken);
      setPendingPayment(null);
      refetchPending();
      await handleSubmitPayment();
    } catch (err: any) {
      showToast(err.response?.data?.message || "Gagal membatalkan pesanan sebelumnya");
    } finally {
      setCancellingPending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col pb-32">
      <PendingPaymentModal
        pending={pendingPayment}
        onContinue={() => {
          if (pendingPayment?.payment_url) window.location.href = pendingPayment.payment_url;
        }}
        onCancelAndNew={handleCancelPendingAndPay}
        onClose={() => setPendingPayment(null)}
        loading={cancellingPending}
      />

      <DoctorUnavailableModal
        show={doctorUnavailable !== null}
        title={doctorUnavailable?.title ?? ""}
        message={doctorUnavailable?.message ?? ""}
        onChooseOther={chooseOtherDoctor}
        onClose={() => setDoctorUnavailable(null)}
      />

      <Confirm
        show={showRemoveVoucherConfirm}
        title="Hapus Voucher?"
        message={
          isInsuranceCovered
            ? "Tanpa voucher, biaya konsultasi tidak ditanggung asuransi dan dibayar penuh. Anda masih bisa memasang voucher lagi sebelum membayar."
            : "Potongan dari voucher akan dihapus dari total pembayaran. Anda masih bisa memasang voucher lagi sebelum membayar."
        }
        confirmText="Hapus"
        cancelText="Batal"
        onConfirm={handleRemoveVoucher}
        onCancel={() => setShowRemoveVoucherConfirm(false)}
      />

      <Confirm
        show={showCancelActiveConfirm}
        title="Batalkan Pesanan?"
        message="Pesanan konsultasi yang belum dibayar akan dibatalkan dan slot dokter dilepas."
        confirmText="Batalkan"
        cancelText="Kembali"
        onConfirm={cancelActivePending}
        onCancel={() => setShowCancelActiveConfirm(false)}
        loading={cancellingPending}
      />

      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999]">
          <div
            className={`flex items-start gap-3 px-5 py-4 rounded-xl shadow-lg text-white text-sm
              max-w-sm w-[90vw] animate-slideDown
              ${toast.type === "error" ? "bg-red-500" : "bg-teal-500"}`}
          >
            <span className="text-lg">{toast.type === "error" ? "❌" : "✅"}</span>
            <div className="flex-1">
              <p className="font-semibold">{toast.type === "error" ? "Gagal" : "Berhasil"}</p>
              <p className="opacity-90">{toast.message}</p>
            </div>
            <button onClick={() => setToast(null)} className="text-white opacity-70 hover:opacity-100 text-lg">×</button>
          </div>
        </div>
      )}

      <HeaderWithBack title="Pembayaran" url={`/disclaimer/${practitionerRoleId}`} />

      {activePending && (
        <div className="px-4 mt-4">
          <PendingPaymentCard
            pending={activePending}
            onContinue={() => {
              if (activePending.payment_url) window.location.href = activePending.payment_url;
            }}
            onCancel={() => setShowCancelActiveConfirm(true)}
            cancelling={cancellingPending}
            onExpired={() => refetchPending()}
          />
        </div>
      )}

      {/* Doctor Card */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
          <img src={imageUrl(checkoutInfo.doctor.photoUrl)} alt="Doctor" className="w-12 h-12 rounded-full object-cover" />
          <div className="flex-1">
            <p className="font-semibold text-gray-800">{checkoutInfo.doctor.name}</p>
            <p className="text-sm text-gray-500">{checkoutInfo.doctor.specialization}</p>
            {isAuto && <p className="text-xs text-teal-600 mt-1">✨ Dokter akan dipilihkan otomatis</p>}
          </div>
        </div>

        <div className="mt-3 bg-pink-100 text-pink-700 rounded-lg px-3 py-2 flex items-center gap-2 text-sm">
          <FaCheckCircle />
          <span>Kami menjamin privasi anda saat berkonsultasi</span>
        </div>

        {isInsuranceCovered && benefitInfo && (
          <BenefitBadge benefit={benefitInfo} consultationPrice={consultationPrice} />
        )}
      </div>

      {/* Rincian Pesanan */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-xl shadow p-4">
          <p className="font-semibold text-gray-800 mb-3">Rincian Pesanan</p>

          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Konsultasi ({serviceType})</span>
            <span>{formatRupiah(consultationPrice)}</span>
          </div>

          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Biaya Transaksi</span>
            <span>{formatRupiah(transactionFee)}</span>
          </div>

          {selectedVoucher && (
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Voucher</span>
              <span className="text-teal-600">- {formatRupiah(voucherDiscount)}</span>
            </div>
          )}

          {isInsuranceCovered && (
            <div className="flex justify-between text-sm mb-2 items-center">
              <span className="flex items-center gap-1.5 text-gray-600">
                <FaShieldAlt className="text-emerald-500 text-xs" />
                Benefit Asuransi
              </span>
              <span className="text-emerald-600 font-medium">Ditanggung Asuransi</span>
            </div>
          )}

          <hr className="my-2" />

          <div className="flex justify-between font-semibold">
            <span>Total</span>
            <div className="text-right">
              {(voucherDiscount > 0 || isInsuranceCovered) && (
                <p className="text-xs text-gray-400 line-through">{formatRupiah(subtotal)}</p>
              )}
              <span className="text-teal-600">{formatRupiah(totalAfterDiscount)}</span>
            </div>
          </div>

          {(voucherDiscount > 0 || isInsuranceCovered) && (
            <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <span className="text-emerald-600 text-lg">🎉</span>
              <span className="text-emerald-700 text-xs font-medium">
                Kamu hemat{" "}
                {formatRupiah(totalSavings)} dari total tagihan!
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Voucher Manual Input */}
      <div className="px-4 mt-4">
        <p className="text-md font-bold mb-2">Kode Voucher</p>
        <div className="bg-white rounded-xl shadow p-4">
          {selectedVoucher ? (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-teal-700 break-words">✅ Voucher {selectedVoucher.code} dipakai</p>
                <p className="text-xs text-gray-500 mt-0.5">Voucher akan diverifikasi kembali saat pembayaran</p>
              </div>
              <button
                type="button"
                onClick={() => setShowRemoveVoucherConfirm(true)}
                disabled={paying}
                className="shrink-0 border border-red-400 text-red-500 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
              >
                Hapus
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={voucherCode}
                onChange={(event) => setVoucherCode(event.target.value)}
                placeholder="Masukkan kode voucher 6 digit"
                className="w-full min-w-0 flex-1 border rounded-lg px-3 py-2"
              />
              <button
                type="button"
                onClick={handleApplyVoucher}
                disabled={applyingVoucher || paying}
                className="w-full sm:w-auto shrink-0 whitespace-nowrap bg-teal-500 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
              >
                {applyingVoucher ? "Memeriksa..." : "Gunakan"}
              </button>
            </div>
          )}
        </div>
      </div>

      {!isFree && (
        <div className="px-4 mt-4">
          <p className="text-md font-bold mb-2">Metode Pembayaran</p>
          <div
            onClick={() =>
              navigate(`/payment-method/${practitionerRoleId}`, {
                state: { isAuto, selectedPayment, selectedVoucher, subtotal, pertanyaan },
              })
            }
            className="bg-white rounded-xl shadow p-4 flex justify-between items-center cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <img
                src={selectedPayment?.displayIcon || assetUrl("icons/payment.svg")}
                alt="payment"
                className="w-8 h-8 object-contain"
              />
              <p className="font-medium">
                {selectedPayment ? selectedPayment.displayName : "Pilih Metode Pembayaran"}
              </p>
            </div>
            <FaChevronRight />
          </div>
          {paymentWindowMinutes != null && (
            <p className="text-xs text-gray-500 mt-2">
              ⏱️ Setelah menekan "Bayar Sekarang", dokter dipesankan untuk Anda selama {paymentWindowMinutes} menit.
              Selesaikan pembayaran sebelum waktu habis agar pesanan tidak kedaluwarsa.
            </p>
          )}
        </div>
      )}

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow px-4 py-3 flex justify-between items-center">
        <div>
          <p className="text-xs text-gray-500">Total Bayar</p>
          <p className="text-teal-600 font-bold text-lg">{formatRupiah(totalAfterDiscount)}</p>
          {isInsuranceCovered && (
            <div className="text-xs text-teal-600 mt-2">
              Asuransi akan diproses setelah pembayaran berhasil
            </div>
          )}
        </div>
        <button
          onClick={handleSubmitPayment}
          disabled={paying}
          className="bg-teal-500 text-white px-6 py-2.5 rounded-xl font-semibold disabled:opacity-50 flex items-center gap-2"
        >
          {isFree ? "Mulai Konsultasi" : "Bayar Sekarang"}
        </button>
      </div>
    </div>
  );
};

export default CheckoutTeleconsultation;
